import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import memberApi, { formatErr } from "../../lib/memberApi";
import { useMemberAuth } from "../../context/MemberAuthContext";
import { ASSETS } from "../../data";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const { setSession } = useMemberAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (!token) setError("Missing or invalid link."); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      const { data } = await memberApi.post("/member/set-password", { token, password });
      await setSession(data.token);
      nav("/portal", { replace: true });
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || "Could not set password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md border border-white/10 bg-[#0a0a0a] p-10"
      >
        <div className="flex items-center gap-3 mb-6">
          <img src={ASSETS.logo} alt="Ghost The Grid" className="h-10 w-10 object-cover rounded-sm" />
          <span className="font-display font-extrabold uppercase tracking-tight">Set Password</span>
        </div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40 mb-8 flex items-center gap-2">
          <KeyRound size={13} /> Unlock your dashboard
        </p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-2">New password</label>
            <input
              data-testid="setpw-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-black/40 border border-white/15 px-4 py-3 font-mono text-sm text-white focus:outline-none focus:border-accent transition-colors"
              placeholder="••••••••"
            />
          </div>
          <div>
            <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 block mb-2">Confirm password</label>
            <input
              data-testid="setpw-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="w-full bg-black/40 border border-white/15 px-4 py-3 font-mono text-sm text-white focus:outline-none focus:border-accent transition-colors"
              placeholder="••••••••"
            />
          </div>
          {error && <p data-testid="setpw-error" className="font-mono text-xs text-accent">{error}</p>}
          <button
            data-testid="setpw-submit"
            type="submit"
            disabled={loading || !token}
            className="w-full flex items-center justify-center gap-2 bg-accent text-white font-mono uppercase tracking-[0.15em] text-sm px-6 py-4 hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <><CheckCircle2 size={16} /> Set Password & Enter</>}
          </button>
        </form>
        <Link to="/portal/login" className="inline-block mt-6 font-mono text-[11px] uppercase tracking-[0.15em] text-white/40 hover:text-white transition-colors">← Back to login</Link>
      </motion.div>
    </div>
  );
}
